/**
 * Lab401: Google "Add to Preferred Sources" button loader.
 * Markup: snippets/google-preferred-source.liquid
 * Styles: assets/lab401-preferred-source.css
 *
 * Google's publisher.js weighs ~250 KB, triggers ~65 follow-up requests and
 * contacts Google with credentials the moment it runs, so this loader:
 *
 *   1. Gates it on visitor consent through Shopify's Customer Privacy API.
 *      Both analytics and marketing must be allowed (the script logs
 *      impressions to Google and shares the visit with a third party).
 *      Visitors outside regulated regions are allowed by default; in the
 *      EU the verdict is "no" until the cookie banner is answered.
 *   2. Fetches it lazily, only once a button scrolls near the viewport.
 *
 * Without consent the wrapper shows a static badge that deep-links to
 * Google's preferences page instead. If consent arrives later
 * (visitorConsentCollected) the badge is swapped for the live button.
 *
 * Google's script initialises only the placeholders present when it runs
 * and never re-scans by itself, which is why it is injected after the DOM
 * is parsed, and why theme-editor section reloads re-run init() through
 * the PREFERRED_SOURCE callback queue.
 */
(function () {
  'use strict';

  var SCRIPT_SRC = 'https://news.google.com/swg/js/v1/publisher.js';
  var WRAPPER = '[data-lab401-preferred-source]';
  var PLACEHOLDER = '[google-add-preferred-source-btn]';
  var ROOT_MARGIN = '400px 0px';

  var allowed = false;
  var scriptRequested = false;
  var observer = null;

  function wrappers() {
    return document.querySelectorAll(WRAPPER);
  }

  function loadScript() {
    if (scriptRequested) return;
    scriptRequested = true;
    var s = document.createElement('script');
    s.async = true;
    s.src = SCRIPT_SRC;
    document.head.appendChild(s);
  }

  /* Ask Google's script to scan for un-initialised placeholders. Works both
     before it loads (PREFERRED_SOURCE is a plain array it drains on start)
     and after (it replaces the array with an object whose push() runs the
     callback immediately). */
  function initPlaceholders() {
    var queue = (window.PREFERRED_SOURCE = window.PREFERRED_SOURCE || []);
    queue.push(function (api) {
      api.init();
    });
  }

  function observe(el) {
    if (!('IntersectionObserver' in window)) {
      loadScript();
      return;
    }
    if (!observer) {
      observer = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            observer.disconnect();
            observer = null;
            loadScript();
            return;
          }
        }
      }, { rootMargin: ROOT_MARGIN });
    }
    observer.observe(el);
  }

  function apply() {
    var els = wrappers();
    for (var i = 0; i < els.length; i++) {
      var wrapper = els[i];
      wrapper.setAttribute('data-mode', allowed ? 'live' : 'fallback');
      if (!allowed) continue;
      var placeholder = wrapper.querySelector(PLACEHOLDER);
      if (!placeholder || placeholder.hasAttribute('data-initialized')) continue;
      if (scriptRequested) {
        initPlaceholders();
      } else {
        observe(placeholder);
      }
    }
  }

  function consentAllows(customerPrivacy) {
    try {
      return !!(
        customerPrivacy.analyticsProcessingAllowed() &&
        customerPrivacy.marketingAllowed()
      );
    } catch (e) {
      return false;
    }
  }

  function start() {
    if (!wrappers().length) return;

    var shopify = window.Shopify;
    if (!shopify || typeof shopify.loadFeatures !== 'function') {
      apply();
      return;
    }

    shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      function (error) {
        var customerPrivacy = window.Shopify.customerPrivacy;
        allowed = !error && customerPrivacy ? consentAllows(customerPrivacy) : false;
        apply();
      }
    );

    document.addEventListener('visitorConsentCollected', function (event) {
      var customerPrivacy = window.Shopify.customerPrivacy;
      var detail = event && event.detail;
      if (customerPrivacy) {
        allowed = consentAllows(customerPrivacy);
      } else if (detail) {
        allowed = !!(detail.analyticsAllowed && detail.marketingAllowed);
      }
      apply();
    });
  }

  /* Theme editor: the footer re-renders on every setting change. */
  document.addEventListener('shopify:section:load', apply);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
